
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import MessageBox from '@nextrequestco/components/src/components/MessageBox/MessageBox.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'
import { mapActions, mapState } from 'vuex'
import { MessageTemplates } from './types/message_boxes'
import throttle from 'lodash/throttle'
import uniqBy from 'lodash/uniqBy'

interface RequestStaff {
  id: number
  // eslint-disable-next-line camelcase
  name_with_email: string
}

interface UserOption {
  value: number
  label: string
}

const ALL_ASSIGNED_STAFF = {
  value: 0,
  label: 'All assigned staff'
}

export default {
  name: 'InternalMessageModal',
  components: { MessageBox, NrSelect, 'nr-editor': NrEditor, NrFormLabel },
  data() {
    return {
      selectedMessageTemplates: [],
      message: '',
      messageRequired: false,
      selectedNotifiedUsers: [ALL_ASSIGNED_STAFF]
    }
  },
  watch: {
    currentAction(newVal) {
      if (newVal === 'comments') {
        this.resetData()
      }
    },
    request(newVal, oldVal) {
      if (newVal.pretty_id !== oldVal.pretty_id) {
        this.resetNotifiedUsers()
      }
    }
  },
  computed: {
    ...mapState('request_show', [
      'displayMsgBox',
      'currentAction',
      'dataFetchState',
      'dataOptions',
      'request',
      'requestStaff'
    ]),

    notifyUserWarning(): boolean {
      return this.selectedNotifiedUsers.length === 0
    },

    disableSubmit(): boolean {
      return !this.selectedNotifiedUsers.length || !this.message.length
    },

    staffOnlyItems(): boolean {
      return this.$ability.can('read', 'staff_only_items')
    },

    notifiableUsers(): UserOption[] {
      const poc = {
        value: this.request.poc.id,
        label: this.request.poc.name_with_email
      }
      const requestStaff = this.requestStaff.map((user: RequestStaff) => ({
        value: user.id,
        label: user.name_with_email
      }))
      return uniqBy([ALL_ASSIGNED_STAFF, poc, ...requestStaff], 'value')
    },

    filteredMessageTemplates(): void {
      return this.dataOptions.message_templates.map(
        (item: MessageTemplates) => ({
          value: item.id,
          label: item.message_template_name,
          description: item.message_template_description
        })
      )
    }
  },
  methods: {
    ...mapActions('request_show', [
      'submitInternalMessage',
      'setDisplayMsgBox',
      'fetchDataOptions',
      'setCurrentAction'
    ]),

    isAllAssignedStaff(val: UserOption): Boolean {
      return val.value === ALL_ASSIGNED_STAFF.value
    },

    resetData() {
      this.resetNotifiedUsers()
      this.selectedMessageTemplates = []
      this.message = ''
      this.messageRequired = false
    },

    deselectUsersToNotify(val: UserOption): void {
      this.selectedNotifiedUsers = this.selectedNotifiedUsers.filter(
        (user: UserOption) => user.value !== val.value
      )
    },

    selectUsersToNotify(val: UserOption): void {
      if (this.isAllAssignedStaff(val)) {
        this.resetNotifiedUsers()
      } else {
        this.selectedNotifiedUsers = [
          ...this.selectedNotifiedUsers,
          val
        ].filter((user: UserOption) => !this.isAllAssignedStaff(user))
      }
    },

    resetNotifiedUsers(): void {
      this.selectedNotifiedUsers = [ALL_ASSIGNED_STAFF]
    },

    setMessageRequired(val: boolean): void {
      this.messageRequired = val
    },

    searchMessageTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({
        searchText,
        dataType: 'message_templates'
      })
    }, 400),

    loadMoreMessageTemplates: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.message_templates.search_text,
        dataType: 'message_templates'
      })
    }, 400),

    disableSubmitMsg(): boolean {
      if (this.staffOnlyItems) {
        return !(this.messageRequired && this.message.length)
      } else {
        return !this.message.length
      }
    },

    setMessageTemplate(reset: boolean = false, templates = []): void {
      if (reset) {
        this.message = this.selectedMessageTemplates
          .map((temp: { description: string }) => temp.description)
          .join('')
      } else {
        this.message = this.message + templates.pop()?.description
      }
    },

    handleSubmitMessage(): void {
      let payload = {
        note_text: this.message,
        note_type: 'staff',
        state: 'staff',
        request_id: this.request.pretty_id,
        message_template_ids: this.selectedMessageTemplates.map(
          (template: { value: number }) => template.value
        )
      }
      // By default if users are not selected, we will not set the users parameter.
      // If no users parameter is set, the backend will send the message to all assigned staff
      // If users are selected, we pass them into the users parameter
      const allAssignedSelected =
        this.selectedNotifiedUsers.length === 1 &&
        this.isAllAssignedStaff(this.selectedNotifiedUsers[0])
      if (!allAssignedSelected) {
        payload = Object.assign(payload, {
          users: this.selectedNotifiedUsers.map(
            (user: UserOption) => user.value
          )
        })
      }
      this.submitInternalMessage(payload).then(() => {
        this.setCurrentAction('')
      })
    }
  }
}
